export type MessageMediaConstraintKey = 'audioMessage' | 'videoMessage' | 'textAttachment';

// Message constraints are intentionally scoped to the message creation flow only.
// Life Review audio responses must keep independent duration guardrails.

export type MediaValidationErrorCode =
  | 'unsupported_type'
  | 'file_too_large';

type MessageMediaConstraint = {
  accept: string;
  maxFileSize: number;
  maxDurationSeconds?: number;
  allowedExtensions: string[];
  allowedMimePrefixes: string[];
};

export const MESSAGE_MEDIA_CONSTRAINTS: Record<MessageMediaConstraintKey, MessageMediaConstraint> = {
  audioMessage: {
    accept: 'audio/*',
    maxFileSize: 100 * 1024 * 1024,
    maxDurationSeconds: 120,
    allowedExtensions: ['wav', 'mp3', 'm4a', 'aac', 'ogg', 'oga', 'webm'],
    allowedMimePrefixes: ['audio/']
  },
  videoMessage: {
    accept: 'video/*',
    maxFileSize: 1024 * 1024 * 1024,
    maxDurationSeconds: 120,
    allowedExtensions: ['mp4', 'webm', 'ogg', 'ogv', 'mov', 'm4v'],
    allowedMimePrefixes: ['video/']
  },
  textAttachment: {
    accept: '.txt,.pdf,.doc,.docx,.png,.jpg,.jpeg',
    maxFileSize: 1024 * 1024,
    allowedExtensions: ['txt', 'pdf', 'doc', 'docx', 'png', 'jpg', 'jpeg'],
    allowedMimePrefixes: [
      'text/plain',
      'application/pdf',
      'application/msword',
      'application/vnd.openxmlformats-officedocument.wordprocessingml.document',
      'image/png',
      'image/jpeg'
    ]
  }
};

const getFileExtension = (fileName: string): string => fileName.split('.').pop()?.toLowerCase() || '';

const isMimeAllowed = (mimeType: string, allowedMimePrefixes: string[]): boolean => {
  if (!mimeType) {
    return false;
  }

  const normalizedMimeType = mimeType.toLowerCase();
  return allowedMimePrefixes.some((allowedMimePrefix) => {
    const normalizedPrefix = allowedMimePrefix.toLowerCase();
    return normalizedPrefix.endsWith('/')
      ? normalizedMimeType.startsWith(normalizedPrefix)
      : normalizedMimeType === normalizedPrefix;
  });
};

export const getMessageMediaConstraint = (key: MessageMediaConstraintKey): MessageMediaConstraint => {
  return MESSAGE_MEDIA_CONSTRAINTS[key];
};

export const isMessageMediaConstraintKey = (value: string): value is MessageMediaConstraintKey => {
  return value === 'audioMessage' || value === 'videoMessage' || value === 'textAttachment';
};

export const detectMessageMediaConstraintKey = (file: File): MessageMediaConstraintKey | null => {
  const extension = getFileExtension(file.name);
  const possibleConstraintKeys = Object.keys(MESSAGE_MEDIA_CONSTRAINTS) as MessageMediaConstraintKey[];

  return (
    possibleConstraintKeys.find((key) => {
      const constraint = MESSAGE_MEDIA_CONSTRAINTS[key];
      return isMimeAllowed(file.type, constraint.allowedMimePrefixes);
    }) ||
    possibleConstraintKeys.find((key) => {
      const constraint = MESSAGE_MEDIA_CONSTRAINTS[key];
      return constraint.allowedExtensions.includes(extension);
    }) ||
    null
  );
};

export const validateMessageMediaFile = async (
  file: File,
  key: MessageMediaConstraintKey
): Promise<{ isValid: true } | { isValid: false; errorCode: MediaValidationErrorCode }> => {
  const constraint = MESSAGE_MEDIA_CONSTRAINTS[key];
  const extension = getFileExtension(file.name);

  const isAllowedType = file.type
    ? isMimeAllowed(file.type, constraint.allowedMimePrefixes)
    : constraint.allowedExtensions.includes(extension);
  if (!isAllowedType) {
    return { isValid: false, errorCode: 'unsupported_type' };
  }

  if (file.size > constraint.maxFileSize) {
    return { isValid: false, errorCode: 'file_too_large' };
  }

  return { isValid: true };
};
